'use client';

import { useEffect, useRef } from 'react';

/**
 * Route-Level Error Boundary
 *
 * Catches unhandled errors within a route segment.
 * Renders INSIDE the existing layout (no <html> or <body> tags).
 * For root layout errors, see global-error.tsx which includes <html>/<body>.
 *
 * CRITICAL: This root error boundary MUST be self-contained with minimal
 * dependencies. If a chunk-loading error or bundler issue caused the original
 * error, importing design system components (Button, lucide-react icons) or
 * heavy error-reporting modules will also fail — causing this error page to
 * crash. That cascade triggers POST /_error 100% error rate in Sentry.
 *
 * Rules:
 * - NO imports from @/components/ui/* (design system may not be loaded)
 * - NO imports from lucide-react (icon chunks may not be loaded)
 * - NO top-level imports from @/lib/error-reporting (heavy dependency chain)
 * - NO top-level imports from @/lib/utils/error-logger-client (imports Sentry)
 * - Error reporting via dynamic import wrapped in try-catch
 * - Uses inline styles as fallback (Tailwind classes for progressive enhancement)
 * - Uses inline SVG instead of icon library
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Prevent double-capture in strict mode
  const capturedRef = useRef(false);

  useEffect(() => {
    // Report error via dynamic imports — if ANY import fails, the error page
    // still renders. We just silently skip reporting.
    if (!capturedRef.current) {
      capturedRef.current = true;

      // Extract component stack from error if available (React attaches this)
      const componentStack = (error as Error & { componentStack?: string }).componentStack;
      const errorInfo = componentStack ? { componentStack } : undefined;

      // Dynamic import of error reporting — does NOT block rendering
      import('@/lib/error-reporting/client')
        .then(({ captureComponentError }) => {
          captureComponentError(error, errorInfo, {
            area: 'route',
            digest: error.digest,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
          }).catch(() => {});
        })
        .catch(() => {
          // Error reporting module failed to load — try Sentry directly
          import('@sentry/nextjs')
            .then((Sentry) => {
              Sentry.captureException(error, {
                tags: { errorBoundary: 'route' },
                extra: { digest: error.digest },
              });
            })
            .catch(() => {
              // Both reporting systems unavailable — silently skip
            });
        });
    }
  }, [error]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      }}
    >
      {/* Glassmorphic Error Card */}
      <div
        className="max-w-md w-full backdrop-blur-md bg-black/70 border border-brand-primary/20 rounded-xl shadow-lg shadow-brand-primary/5 p-8 text-center"
        style={{
          maxWidth: '400px',
          width: '90%',
          padding: '32px',
          borderRadius: '16px',
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212, 175, 55, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          textAlign: 'center',
        }}
      >
        {/* Error Icon — inline SVG, no lucide-react dependency */}
        <div
          className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center"
          style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
        </div>

        {/* Error Title */}
        <h1
          className="text-3xl font-bold text-white mb-4"
          style={{ margin: '0 0 16px', fontSize: '24px', fontWeight: 'bold', color: '#ffffff' }}
        >
          Something went wrong
        </h1>

        {/* Error Message */}
        <p
          className="text-gray-400 mb-2 text-sm"
          style={{ margin: '0 0 8px', fontSize: '14px', color: '#9ca3af' }}
        >
          {error.message || 'An unexpected error occurred. Please try again.'}
        </p>

        {/* Error Digest (if available) */}
        {error.digest && (
          <p
            className="text-gray-500 text-xs mb-6 font-mono"
            style={{ margin: '0 0 24px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}
          >
            Error ID: {error.digest}
          </p>
        )}

        {/* Action Buttons — native buttons, no design system dependency */}
        <div
          className="flex flex-col gap-3 mt-8"
          style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}
        >
          <button
            onClick={reset}
            className="w-full bg-gradient-to-r from-brand-primary to-brand-accent text-black font-bold hover:from-brand-primary hover:to-brand-accent transition-all duration-200 rounded-lg py-3 px-6"
            style={{
              width: '100%',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #d4af37 0%, #f5d76e 100%)',
              color: '#000000',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            Try Again
          </button>

          <button
            onClick={() => window.location.href = '/'}
            className="w-full border border-brand-primary/30 text-gray-300 hover:bg-brand-primary/10 hover:border-brand-primary/50 transition-all duration-200 rounded-lg py-3 px-6"
            style={{
              width: '100%',
              padding: '12px 24px',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '8px',
              background: 'transparent',
              color: '#d1d5db',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
            }}
          >
            Go to Home
          </button>
        </div>

        {/* Help Text */}
        <p
          className="text-gray-500 text-xs mt-6"
          style={{ margin: '24px 0 0', fontSize: '12px', color: '#6b7280' }}
        >
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}
